import React, { memo, useCallback } from 'react';

import { Box, TableSortLabel, Tooltip, Typography } from '@mui/material';
import ColumnFilterElement from 'app/shared/componentsRestyled/common/ColumnFilterElement';
import {
  SORT_DIRECTIONS,
  SORT_LABEL_ALIGNMENT,
} from 'app/shared/componentsRestyled/common/TooltipedSortLabel/TooltipedSortLabel.constants';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import { InfoIcon } from '../icons';

import SortIconCDS from './SortIconCDS';
import { TOOLTIP_POPPER_PROPS } from './TooltipedSortLabelCDS.constants';
import {
  getFilterWrapperStyles,
  getWrapperStyles,
  iconWrapperStyles,
  tooltipIconStyles,
} from './TooltipedSortLabelCDS.styles';

const defaultObject = {};

const TooltipedSortLabelCDS = ({
  isSort = false,
  id,
  label = null,
  order = defaultObject,
  onRequestSort = null,
  align = SORT_LABEL_ALIGNMENT.LEFT,
  disabled = false,
  tooltip = null,
  tooltipIcon: TooltipIconComponent = InfoIcon,
  filter = null,
  columnKey = null,
  columnFilters = null,
  onColumnFiltersChange = null,
  ...props
}) => {
  const getSortDirection = useCallback(() => {
    const isInitial = order.id !== id;

    return isInitial ? SORT_DIRECTIONS.NONE : order.direction;
  }, [order, id]);

  const tooltipIconElement = tooltip ? (
    <Tooltip PopperProps={TOOLTIP_POPPER_PROPS} title={tooltip}>
      <Box sx={tooltipIconStyles}>
        <TooltipIconComponent size={IconSize[16]} />
      </Box>
    </Tooltip>
  ) : null;

  const hasFilter = !!filter && !!onColumnFiltersChange;

  const content = (
    <Typography data-truncated className="truncate" color="text.primary" variant="medium14">
      {label}
    </Typography>
  );

  const filterElement = hasFilter ? (
    <ColumnFilterElement
      columnFilters={columnFilters}
      columnKey={columnKey}
      filter={filter}
      onColumnFiltersChange={onColumnFiltersChange}
    />
  ) : null;

  if (!isSort) {
    return (
      <Box sx={hasFilter ? getFilterWrapperStyles(align) : getWrapperStyles(align)}>
        {content}
        {tooltipIconElement}
        {filterElement}
      </Box>
    );
  }

  const direction = getSortDirection();

  return (
    <TableSortLabel
      {...props}
      hideSortIcon
      className="text-center"
      data-id={id}
      direction={order.direction}
      sx={hasFilter ? getFilterWrapperStyles(align) : getWrapperStyles(align)}
      onClick={disabled ? undefined : onRequestSort}
    >
      {content}
      <Box className="sort-icon-wrapper" sx={iconWrapperStyles}>
        <SortIconCDS direction={direction} />
      </Box>
      {tooltipIconElement}
      {filterElement}
    </TableSortLabel>
  );
};

TooltipedSortLabelCDS.propTypes = {
  id: PropTypes.string.isRequired,
  align: PropTypes.oneOf(Object.values(SORT_LABEL_ALIGNMENT)),
  isSort: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.node,
  order: PropTypes.object,
  tooltip: PropTypes.node,
  tooltipIcon: PropTypes.elementType,
  filter: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  columnKey: PropTypes.string,
  columnFilters: PropTypes.object,
  onRequestSort: PropTypes.func,
  onColumnFiltersChange: PropTypes.func,
};

export default memo(TooltipedSortLabelCDS);
